import type {
  EdgeTreatment,
  HeightmapAsset,
  LedSpec,
  PanelDesign,
  ParamValues,
  PatternLayer,
  ReliefFinish,
  TextureSource,
} from '../types';
import { clamp } from '../math/scalar';
import { resolveParams } from '../params';
import { getPattern } from '../patterns/registry';
import { getShape } from '../shapes/registry';
import {
  LED_TYPES,
  PANEL_MATERIALS,
  checkBlending,
  checkTransmission,
  ledType,
  panelMaterial,
} from '../led/light';
import { CONSTRAINTS } from './constraints';
import {
  DESIGN_VERSION,
  createDefaultDesign,
  createId,
  defaultEdgeTreatment,
  defaultFinish,
  defaultLedSpec,
  identityTransform,
} from './defaults';

/**
 * Design validation.
 *
 * A design can arrive from a file, a URL, an older version of the app or a
 * generator, so nothing downstream may assume it is well-formed. Normalising
 * repairs rather than rejects: unknown layers are dropped, out-of-range numbers
 * are clamped and missing fields fall back to the defaults, so a slightly stale
 * file still opens.
 */

export interface NormalizeResult {
  design: PanelDesign;
  /** Human-readable notes about what had to be repaired. */
  warnings: string[];
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}

function numberOr(value: unknown, fallback: number): number {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function clampTo(value: unknown, range: { min: number; max: number }, fallback: number): number {
  return clamp(numberOr(value, fallback), range.min, range.max);
}

function normalizeLayer(raw: unknown, warnings: string[]): PatternLayer | null {
  const record = asRecord(raw);
  const patternId = String(record.patternId ?? '');
  const definition = getPattern(patternId);

  if (!definition) {
    warnings.push(`Dropped layer with unknown pattern "${patternId}".`);
    return null;
  }

  const transform = asRecord(record.transform);
  const scale = numberOr(transform.scale, 1);

  return {
    id: typeof record.id === 'string' ? record.id : createId(),
    patternId,
    params: resolveParams(definition.params, asRecord(record.params) as ParamValues),
    enabled: record.enabled !== false,
    amount: clamp(numberOr(record.amount, 1), 0, 1),
    blend: (typeof record.blend === 'string' ? record.blend : 'replace') as PatternLayer['blend'],
    transform: {
      ...identityTransform(),
      offsetX: numberOr(transform.offsetX, 0),
      offsetY: numberOr(transform.offsetY, 0),
      rotation: numberOr(transform.rotation, 0),
      // A zero or negative scale would collapse the whole field to one point.
      scale: Math.abs(scale) < 1e-3 ? 1 : scale,
      mirrorX: transform.mirrorX === true,
      mirrorY: transform.mirrorY === true,
    },
  };
}

function normalizeSource(raw: unknown, warnings: string[]): TextureSource {
  const record = asRecord(raw);
  const layers = Array.isArray(record.layers)
    ? record.layers.map((layer) => normalizeLayer(layer, warnings)).filter(Boolean)
    : [];

  const kind = record.kind;
  const source: TextureSource = {
    kind:
      kind === 'pack' || kind === 'image' || kind === 'ai' || kind === 'custom' ? kind : 'custom',
    label: typeof record.label === 'string' && record.label ? record.label : 'Custom',
    layers: layers as PatternLayer[],
  };

  if (typeof record.presetId === 'string') source.presetId = record.presetId;
  if (typeof record.prompt === 'string') source.prompt = record.prompt;

  if (source.layers.length === 0) {
    warnings.push('The design had no usable layers; falling back to a flat panel.');
    source.layers = [
      {
        id: createId(),
        patternId: 'flat',
        params: resolveParams(getPattern('flat')?.params ?? [], {}),
        enabled: true,
        amount: 1,
        blend: 'replace',
        transform: identityTransform(),
      },
    ];
  }

  return source;
}

function normalizeEdge(raw: unknown): EdgeTreatment {
  const record = asRecord(raw);
  const mode = record.mode;
  const fallback = defaultEdgeTreatment();
  return {
    mode: mode === 'flat-border' || mode === 'chamfer' ? mode : 'seamless',
    width: clampTo(record.width, CONSTRAINTS.edgeWidth, fallback.width),
  };
}

function normalizeFinish(raw: unknown): ReliefFinish {
  const record = asRecord(raw);
  const fallback = defaultFinish();
  return {
    gamma: clampTo(record.gamma, CONSTRAINTS.gamma, fallback.gamma),
    contrast: clampTo(record.contrast, CONSTRAINTS.contrast, fallback.contrast),
    terraceSteps: Math.round(
      clampTo(record.terraceSteps, CONSTRAINTS.terraceSteps, fallback.terraceSteps),
    ),
    invert: record.invert === true,
    autoLevel: record.autoLevel !== false,
  };
}

function normalizeLed(raw: unknown): LedSpec {
  const record = asRecord(raw);
  const fallback = defaultLedSpec();
  const layout = record.layout;

  const mounting = record.mounting;
  const spec: LedSpec = {
    enabled: record.enabled === true,
    mounting: mounting === 'lightbox' ? 'lightbox' : 'channel',
    channelDepth: clampTo(record.channelDepth, CONSTRAINTS.channelDepth, fallback.channelDepth),
    airGap: clampTo(record.airGap, CONSTRAINTS.airGap, fallback.airGap),
    ledType: LED_TYPES.some((type) => type.id === record.ledType)
      ? String(record.ledType)
      : fallback.ledType,
    panelMaterial: PANEL_MATERIALS.some((material) => material.id === record.panelMaterial)
      ? String(record.panelMaterial)
      : fallback.panelMaterial,
    frameWall: clampTo(record.frameWall, CONSTRAINTS.frameWall, fallback.frameWall),
    frameFloor: clampTo(record.frameFloor, CONSTRAINTS.frameFloor, fallback.frameFloor),
    layout:
      layout === 'vertical' || layout === 'grid' || layout === 'perimeter' ? layout : 'horizontal',
    stripWidth: clampTo(record.stripWidth, CONSTRAINTS.stripWidth, fallback.stripWidth),
    spacing: clamp(numberOr(record.spacing, fallback.spacing), 0.25, 12),
    offset: clamp(numberOr(record.offset, fallback.offset), 0, 1),
    inset: clampTo(record.inset, CONSTRAINTS.ledInset, fallback.inset),
    passThrough: record.passThrough !== false,
  };

  // A design written before the light box existed carries a channel depth and
  // a diffuser thickness, and no mounting. Both mountings exist again, so the
  // channel depth is now kept rather than dropped and the design opens as what
  // it always was. The diffuser is no longer a setting: it is whatever the
  // backing plate leaves over the channel, which is the same number stated
  // where it can be acted on.
  if (record.mounting === undefined && record.channelDepth !== undefined) {
    spec.mounting = 'channel';
  }

  return spec;
}

function normalizeAssets(raw: unknown, warnings: string[]): HeightmapAsset[] {
  if (!Array.isArray(raw)) return [];
  const assets: HeightmapAsset[] = [];

  for (const entry of raw) {
    const record = asRecord(entry);
    const width = Math.round(numberOr(record.width, 0));
    const height = Math.round(numberOr(record.height, 0));
    const data = record.data;

    const values =
      data instanceof Float32Array
        ? data
        : Array.isArray(data)
          ? Float32Array.from(data as number[])
          : null;

    if (!values || width <= 0 || height <= 0 || values.length !== width * height) {
      warnings.push(`Dropped malformed image asset "${String(record.id ?? 'unnamed')}".`);
      continue;
    }

    assets.push({
      id: typeof record.id === 'string' ? record.id : createId('asset'),
      name: typeof record.name === 'string' ? record.name : 'Image',
      width,
      height,
      data: values,
    });
  }

  return assets;
}

export function normalizeDesign(raw: unknown): NormalizeResult {
  const warnings: string[] = [];
  const record = asRecord(raw);
  const fallback = createDefaultDesign();

  const panel = asRecord(record.panel);
  const grid = asRecord(record.grid);

  const shapeId = String(panel.shapeId ?? fallback.panel.shapeId);
  if (!getShape(shapeId)) {
    warnings.push(`Unknown panel shape "${shapeId}"; using "${fallback.panel.shapeId}".`);
  }

  const size = clampTo(panel.size, CONSTRAINTS.panelSize, fallback.panel.size);
  if (numberOr(panel.size, size) !== size) {
    warnings.push(
      `Panel size was clamped to ${size} mm (allowed: ${CONSTRAINTS.panelSize.min}–${CONSTRAINTS.panelSize.max} mm).`,
    );
  }

  const design: PanelDesign = {
    version: DESIGN_VERSION,
    name: typeof record.name === 'string' && record.name ? record.name : fallback.name,
    grid: {
      columns: Math.round(clampTo(grid.columns, CONSTRAINTS.columns, fallback.grid.columns)),
      rows: Math.round(clampTo(grid.rows, CONSTRAINTS.rows, fallback.grid.rows)),
    },
    panel: {
      shapeId: getShape(shapeId) ? shapeId : fallback.panel.shapeId,
      size,
      gap: clampTo(panel.gap, CONSTRAINTS.gap, fallback.panel.gap),
      baseThickness: clampTo(
        panel.baseThickness,
        CONSTRAINTS.baseThickness,
        fallback.panel.baseThickness,
      ),
      reliefDepth: clampTo(panel.reliefDepth, CONSTRAINTS.reliefDepth, fallback.panel.reliefDepth),
      segments: Math.round(clampTo(panel.segments, CONSTRAINTS.segments, fallback.panel.segments)),
      edge: normalizeEdge(panel.edge),
    },
    source: normalizeSource(record.source, warnings),
    finish: normalizeFinish(record.finish),
    led: normalizeLed(record.led),
    assets: normalizeAssets(record.assets, warnings),
  };

  // Backlighting can produce parts that print perfectly and light badly, so
  // the optical checks are surfaced here as well as in the UI.
  if (design.led.enabled) {
    const type = ledType(design.led.ledType);
    if (design.led.mounting === 'lightbox') {
      const blending = checkBlending(design.led.airGap, type);
      if (!blending.ok) {
        warnings.push(
          `At a ${design.led.airGap} mm air gap, a ${type.label} strip will show as individual ` +
            `LEDs rather than an even line. Raise the gap to ${Math.ceil(blending.requiredMm)} mm or use a COB strip.`,
        );
      }
    } else if (type.pitchMm > 4) {
      // A channel holds the strip a millimetre or two under the surface, and
      // no groove is deep enough to blend emitters that far apart. There is no
      // number to raise, so the only honest advice is a different strip or a
      // different mounting — and a COB strip needs no warning at all, which is
      // why this does not fire on every channel design.
      warnings.push(
        `A channel puts a ${type.label} strip just under the surface, where its emitters will ` +
          'read as separate dots. A COB strip is continuous, or the light-box mounting gives them room to mix.',
      );
    }
    const material = panelMaterial(design.led.panelMaterial);
    if (material.opaque) {
      warnings.push(
        'The panel material is opaque, so almost no light will reach the front. Backlighting needs a translucent or white filament.',
      );
    } else {
      // Only the unlit case is a warning. A relief whose peaks go dark still
      // lights the wall and reads as a silhouette, which is a look rather than
      // a fault — the UI and the export readme describe it, but it is nothing
      // to flag on load.
      const light = checkTransmission(
        design.panel.baseThickness,
        design.panel.reliefDepth,
        material,
      );
      if (!light.ok) {
        warnings.push(
          `A ${design.panel.baseThickness} mm backing plate in ${material.label} lets only ` +
            `${Math.round(light.brightest * 100)}% of the light through, so the wall will not read as lit. ` +
            `Thin the plate to ${light.maxBaseMm.toFixed(1)} mm or print in a clearer filament.`,
        );
      }
    }
  }

  // An image layer pointing at a bitmap that never made it into the file would
  // silently render as a flat panel, so it is worth saying out loud.
  const assetIds = new Set(design.assets.map((asset) => asset.id));
  for (const layer of design.source.layers) {
    const reference = layer.params.asset;
    if (typeof reference === 'string' && reference && !assetIds.has(reference)) {
      warnings.push(`Image layer references a missing bitmap ("${reference}").`);
    }
  }

  return { design, warnings };
}
